import { el } from "../dom.ts";
import { button } from "./button.ts";

/**
 * Confirmation dialog over the native `<dialog>` element.
 *
 * Built on the platform element rather than a custom overlay because the
 * browser supplies the focus trap, the Escape key, background inertness and the
 * correct role — four things a hand-rolled modal reliably gets wrong, and each
 * of which is an accessibility defect rather than a rough edge.
 *
 * Used only where the UI UX Pro Max `confirmation-dialogs` rule applies: an
 * action that discards work or cannot be undone.
 */

export interface ConfirmDialogOptions {
  readonly title: string;
  readonly text: string;
  readonly confirmLabel: string;
  readonly cancelLabel?: string;
  readonly tone?: "default" | "danger";
  readonly onConfirm: () => void;
}

export interface ConfirmDialog {
  readonly root: HTMLDialogElement;
  open(): void;
  close(): void;
}

export function confirmDialog(options: ConfirmDialogOptions): ConfirmDialog {
  const root = el("dialog", { class: "nura-dialog" });

  const cancel = button({
    label: options.cancelLabel ?? "Keep it",
    variant: "secondary",
    onClick: () => {
      root.close();
    },
  });

  const confirm = button({
    label: options.confirmLabel,
    variant: options.tone === "danger" ? "danger" : "primary",
    onClick: () => {
      root.close();
      options.onConfirm();
    },
  });

  root.append(
    el("div", { class: "nura-dialog__body" }, [
      el("h2", { class: "nura-dialog__title", text: options.title }),
      el("p", { class: "nura-dialog__text", text: options.text }),
    ]),
    // Confirm sits last so the destructive action is not the first thing a
    // pointer lands on, and is visually separated from the safe choice.
    el("div", { class: "nura-dialog__actions" }, [cancel, confirm]),
  );

  return {
    root,
    open(): void {
      root.showModal();
      cancel.focus();
    },
    close(): void {
      root.close();
    },
  };
}
